function decryptUrlClient(encryptedData: string, key: string): string {
  try {
    const decoded = atob(encryptedData);
    let decrypted = '';
    const keyLength = key.length;

    for (let i = 0; i < decoded.length; i++) {
      decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % keyLength));
    }

    return decrypted;
  } catch (error) {
    console.error('Client decryption error:', error);
    throw new Error('Failed to decrypt URL');
  }
}

export interface UploadProgressCallback {
  onProgress?: (progress: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

export interface FileUploadResult {
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Date;
  s3Key: string;
}

export interface FileDeleteResult {
  success: boolean;
  s3Key: string;
  message: string;
}

export class UnifiedUploader {
  private static async callAPI(data: any) {
    const response = await fetch('/api/upload/files', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API call failed');
    }

    return await response.json();
  }

  static async uploadFile(
    file: File,
    userId: string,
    docId?: string,
    callbacks?: UploadProgressCallback,
    path?: string | null,
  ): Promise<FileUploadResult> {
    try {
      console.log('Starting secure upload:', file.name, `(${Math.round(file.size / 1024 / 1024)}MB)`);

      // Step 1: Initiate upload
      const initResult = await this.callAPI({
        action: 'initiate',
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        userId,
        docId,
        path
      });

      const { uploadType, fileUrl, s3Key } = initResult;

      let finalUrl: string;

      if (uploadType === 'direct') {
        // Single file upload with encrypted URL
        finalUrl = await this.uploadDirect(file, initResult, callbacks);
      } else {
        // Multipart upload with encrypted part URLs
        finalUrl = await this.uploadMultipart(file, initResult, callbacks);
      }

      const result: FileUploadResult = {
        name: file.name,
        url: finalUrl,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        s3Key,
      };

      callbacks?.onSuccess?.(finalUrl);
      return result;

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Upload failed');
      callbacks?.onError?.(err);
      throw err;
    }
  }

  private static async uploadDirect(
    file: File,
    initResult: any,
    callbacks?: UploadProgressCallback
  ): Promise<string> {
    // DECRYPT THE URL BEFORE USE
    const presignedUrl = decryptUrlClient(initResult.encryptedUrl, initResult.encryptionKey);
    console.log('Using decrypted URL for direct upload');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          callbacks?.onProgress?.(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          callbacks?.onProgress?.(100);
          console.log('Direct upload completed successfully');
          resolve(initResult.fileUrl);
        } else {
          reject(new Error(`Direct upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during direct upload'));

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  private static async uploadMultipart(
    file: File,
    initResult: any,
    callbacks?: UploadProgressCallback
  ): Promise<string> {
    const { uploadId, s3Key, partSize, totalParts, encryptionKey } = initResult;
    const parts: Array<{ ETag: string; PartNumber: number }> = [];
    let uploadedBytes = 0;

    console.log(`Starting secure multipart upload: ${totalParts} parts of ${Math.round(partSize / 1024 / 1024)}MB each`);

    try {
      // Upload all parts with encrypted URLs
      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const partData = file.slice(start, end);

        console.log(`Uploading encrypted part ${partNumber}/${totalParts} (${Math.round(partData.size / 1024 / 1024)}MB)`);

        // Get encrypted presigned URL for this part
        const partUrlResult = await this.callAPI({
          action: 'get-part-url',
          s3Key,
          uploadId,
          partNumber,
        });

        // 🔓 DECRYPT THE PART URL BEFORE USE
        const presignedUrl = decryptUrlClient(partUrlResult.encryptedUrl, partUrlResult.encryptionKey);

        // Upload the part using decrypted URL
        const etag = await new Promise<string>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.onload = () => {
            if (xhr.status === 200) {
              const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') || '';
              resolve(etag);
            } else {
              reject(new Error(`Part ${partNumber} upload failed: ${xhr.status}`));
            }
          };

          xhr.onerror = () => reject(new Error(`Network error uploading part ${partNumber}`));

          xhr.open('PUT', presignedUrl);
          xhr.send(partData);
        });

        parts.push({ ETag: etag, PartNumber: partNumber });
        uploadedBytes += partData.size;

        // Update overall progress
        const progress = Math.round((uploadedBytes / file.size) * 100);
        callbacks?.onProgress?.(progress);
      }

      // Complete the multipart upload
      console.log('Completing secure multipart upload...');
      const completeResult = await this.callAPI({
        action: 'complete',
        s3Key,
        uploadId,
        parts,
      });

      console.log('Secure multipart upload completed');
      return completeResult.url;

    } catch (error) {
      // Abort the multipart upload on error
      console.log('Aborting multipart upload due to error');
      try {
        await this.callAPI({
          action: 'abort',
          s3Key,
          uploadId,
        });
      } catch (abortError) {
        console.error('Failed to abort multipart upload:', abortError);
      }
      throw error;
    }
  }

  /**
 * Delete a file from S3 using its s3Key
 * @param s3Key - The S3 key of the file to delete
 * @returns Promise<FileDeleteResult> - Result of the deletion operation
 */
  static async deleteFile(s3Key: string): Promise<FileDeleteResult> {
    try {
      console.log('Starting secure deletion for S3 key:', s3Key);
      if (!s3Key || s3Key.trim() === '') {
        return { success: false, s3Key, message: 'Invalid S3 key' };
      }

      const deleteResult = await this.callAPI({
        action: 'delete',
        s3Key,
      });

      console.log('File deleted successfully from S3:', s3Key);
      return {
        success: deleteResult.success,
        s3Key: deleteResult.s3Key,
        message: deleteResult.message,
      };

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Delete failed');
      console.error('Failed to delete file from S3:', s3Key, err);
      throw err;
    }
  }

  static async getSignedDownloadUrl(path: string) {
    console.log('Getting signed download URL for:', path);
    try {
      const result = await this.callAPI({
        action: 'get-download-url',
        s3Key: path,
      });
      return result.url;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Get download URL failed');
      console.error('Failed to get download URL:', path, err);
      throw err;
    }
  }

}